import { ItemRequirementLine } from '@/ui/components/item-requirement-line';
import { ItemType } from 'eolib';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import type {
  ForgeDropSlot,
  ForgeElementMapping,
  ForgeItemEnhancement,
  ForgeOpenPayload,
  ForgeReply,
  ForgeStatGemMapping,
} from '@/controllers/forge-controller';
import type { ForgeSlotKey } from '@/forge-protocol';
import { Button, ItemIcon } from '@/ui/components';
import { UI_ITEM_BG, UI_PANEL_BORDER } from '@/ui/consts';
import { useClient } from '@/ui/context';
import { useItemDrag } from '@/ui/in-game';
import { getItemMeta } from '@/utils';
import { DialogBase } from './dialog-base';

const ELEMENT_NAMES: Record<number, string> = {
  1: 'Light',
  2: 'Dark',
  3: 'Earth',
  4: 'Wind',
  5: 'Water',
  6: 'Fire',
};

const FALLBACK_FORGE_DATA: ForgeOpenPayload = {
  title: 'Ron The Smith Forge',
  message: 'Ron The Smith: place gear in the Gear Slot. Use Luna Gems for + upgrades, crystals/orbs for elements, and colored gems for random stats.',
  luna_gem_item_id: 232,
  max_upgrade_level: 100,
  stat_socket_count: 6,
  crystal_success_bonus_percent: 2,
  crystal_max_count: 10,
  failure_wipe_percent: 10,
  safe_upgrade_checkpoint: 5,
  weapon_max_element_slots: 2,
  second_element_unlock_level: 5,
  element_add_gold_cost: 1000,
  element_remove_gold_cost: 500,
  random_option_success_percent: 0,
  stat_gem_gold_cost: 750,
  stat_gem_base_success_percent: 40,
  stat_gem_success_bonus_percent: 12,
  stat_gem_max_success_percent: 90,
  stat_gem_max_count: 5,
  element_mappings: [
    { item_id: 78, element: 6, power: 6, label: 'Fire Crystal' },
    { item_id: 71, element: 5, power: 6, label: 'Water Crystal' },
    { item_id: 72, element: 3, power: 6, label: 'Earth Crystal' },
    { item_id: 74, element: 3, power: 4, label: 'Forest Crystal' },
    { item_id: 77, element: 4, power: 6, label: 'Air Crystal' },
    { item_id: 73, element: 2, power: 6, label: 'Dark Crystal' },
    { item_id: 79, element: 1, power: 6, label: 'Light Crystal' },
    { item_id: 11, element: 1, power: 3, label: 'Orb of Light' },
  ],
  stat_gem_mappings: [
    { item_id: 234, option_types: ['damage', 'accuracy'], min_value: 1, max_value: 2, tier: 1, label: 'Dark Gem' },
    { item_id: 233, option_types: ['armor', 'con'], min_value: 1, max_value: 2, tier: 1, label: 'Green Gem' },
    { item_id: 226, option_types: ['accuracy', 'evade', 'agi'], min_value: 1, max_value: 2, tier: 1, label: 'Yellow Gem' },
    { item_id: 227, option_types: ['tp', 'wis'], min_value: 1, max_value: 3, tier: 1, label: 'Blue Gem' },
    { item_id: 228, option_types: ['int', 'tp'], min_value: 1, max_value: 2, tier: 1, label: 'Purple Gem' },
    { item_id: 229, option_types: ['hp', 'cha'], min_value: 1, max_value: 3, tier: 1, label: 'Pink Gem' },
    { item_id: 230, option_types: ['damage', 'str'], min_value: 1, max_value: 2, tier: 1, label: 'Red Gem' },
    { item_id: 231, option_types: ['damage', 'con'], min_value: 1, max_value: 2, tier: 1, label: 'Orange Gem' },
    { item_id: 337, option_types: ['int', 'wis'], min_value: 1, max_value: 2, tier: 1, label: 'Ice Gem' },
  ],
};

type ForgeAction = 'upgrade' | 'element' | 'stat' | 'remove' | 'recycle' | 'resetstats';

type ForgeDropSlotViewProps = {
  slot: ForgeDropSlot;
  title: string;
  hint: string;
  itemId: number;
  enhancement?: ForgeItemEnhancement;
  accepts: (itemId: number) => boolean;
  onClear: () => void;
  onPick: () => void;
};

function getDisplayName(client: ReturnType<typeof useClient>, itemId: number): string {
  return client.getEifRecordById(itemId)?.name ?? `Item #${itemId}`;
}

function slotForItemType(type: ItemType): ForgeSlotKey | null {
  switch (type) {
    case ItemType.Weapon:
      return 'weapon';
    case ItemType.Armor:
      return 'armor';
    case ItemType.Shield:
      return 'shield';
    case ItemType.Hat:
      return 'hat';
    case ItemType.Boots:
      return 'boots';
    case ItemType.Gloves:
      return 'gloves';
    default:
      return null;
  }
}

function visibleUpgradeLevel(name?: string): number {
  const match = name?.trim().match(/\+(\d+)$/);
  return match ? Number.parseInt(match[1], 10) || 0 : 0;
}

function formatGold(amount: number): string {
  return amount.toLocaleString();
}

function getElementName(mapping?: ForgeElementMapping): string {
  if (!mapping) {
    return 'Element';
  }

  return mapping.element_name || ELEMENT_NAMES[mapping.element] || `Element ${mapping.element}`;
}

function formatStatTypes(mapping?: ForgeStatGemMapping): string {
  if (!mapping?.option_types?.length) {
    return 'random stat';
  }

  return mapping.option_types
    .map((option) => {
      switch (option) {
        case 'accuracy':
          return 'Accuracy';
        case 'evade':
          return 'Evade';
        case 'armor':
          return 'Armor';
        case 'hp':
          return 'HP';
        case 'tp':
          return 'TP';
        case 'str':
          return 'STR';
        case 'int':
          return 'INT';
        case 'wis':
          return 'WIS';
        case 'agi':
          return 'AGI';
        case 'con':
          return 'CON';
        case 'cha':
          return 'CHA';
        case 'damage':
          return 'Damage';
        default:
          return option;
      }
    })
    .join(' / ');
}

function statGemSuccessChance(forgeData: ForgeOpenPayload, gemCount: number): number {
  const base = forgeData.stat_gem_base_success_percent ?? 40;
  const bonus = forgeData.stat_gem_success_bonus_percent ?? 12;
  const max = forgeData.stat_gem_max_success_percent ?? 90;
  return Math.max(0, Math.min(max, base + (Math.max(1, gemCount) - 1) * bonus));
}

function ForgeDropSlotView({
  slot,
  title,
  hint,
  itemId,
  enhancement,
  accepts,
  onClear,
  onPick,
}: ForgeDropSlotViewProps) {
  const client = useClient();
  const { currentDrag } = useItemDrag();
  const record = itemId > 0 ? client.getEifRecordById(itemId) : undefined;
  const canDrop = currentDrag?.source && accepts(currentDrag.itemId);
  const isDragging = Boolean(currentDrag?.source);
  const meta = record ? getItemMeta(record, itemId, enhancement) : [];
  const displayName = enhancement?.display_name || record?.name || '';
  const enhancementStats = enhancement?.stats ?? [];
  const enhancementElements = enhancement?.elements ?? [];

  return (
    <div class='flex min-w-0 flex-col gap-1'>
      <div class='flex items-center justify-between'>
        <span class='font-semibold text-xs uppercase tracking-wide opacity-75'>
          {title}
        </span>
        {itemId > 0 && (
          <button
            type='button'
            class='rounded px-2 py-0.5 text-[10px] opacity-70 hover:bg-base-content/10 hover:opacity-100'
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>
      <div
        data-forge-drop={slot}
        role='button'
        tabIndex={0}
        class={`min-h-32 cursor-pointer rounded border-2 border-dashed p-3 transition-colors ${
          isDragging
            ? canDrop
              ? 'border-success bg-success/15'
              : 'border-error bg-error/10'
            : `${UI_PANEL_BORDER} bg-base-300/40`
        }`}
        onClick={(event) => {
          event.stopPropagation();
          if (!isDragging) onPick();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onPick();
          }
        }}
      >
        {record ? (
          <div class='flex gap-3'>
            <div
              class={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-1`}
            >
              <ItemIcon
                graphicId={record.graphicId}
                upgradeLevel={enhancement?.upgrade_level ?? 0}
                alt={record.name}
                class='max-h-full max-w-full object-contain'
              />
            </div>
            <div class='min-w-0 flex-1'>
              <div class='wrap-break-word font-semibold text-sm'>{displayName}</div>
              <div class='text-[11px] opacity-60'>
                Item #{itemId}
                {enhancement && (
                  <span> · {enhancement.equipped ? 'equipped bonuses active' : 'inventory item'}</span>
                )}
              </div>
              {enhancementElements.length > 0 && (
                <div class='mt-1 flex flex-wrap gap-1'>
                  {enhancementElements.map((element) => (
                    <span
                      key={`${element.slot}-${element.element}`}
                      class='rounded bg-info/20 px-1.5 py-0.5 text-[10px] text-info'
                    >
                      {element.element} +{element.power}
                    </span>
                  ))}
                </div>
              )}
              {enhancementStats.length > 0 && (
                <div class='mt-1 flex flex-wrap gap-1'>
                  {enhancementStats.map((line) => (
                    <span
                      key={line}
                      class='rounded bg-success/20 px-1.5 py-0.5 text-[10px] text-success'
                    >
                      <ItemRequirementLine line={line} item={record} client={client} />
                    </span>
                  ))}
                </div>
              )}
              {meta.length > 0 && (
                <div class='mt-1 flex flex-wrap gap-1'>
                  {meta.map((line) => (
                    <span
                      key={line}
                      class='rounded bg-base-content/10 px-1.5 py-0.5 text-[10px]'
                    >
                      <ItemRequirementLine line={line} item={record} client={client} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div class='flex min-h-24 flex-col items-center justify-center gap-2 px-4 text-center text-xs leading-relaxed opacity-65'>
            <span>{hint}</span>
            <span class='rounded bg-base-content/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide'>
              Tap to pick
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

type ForgeInventoryPickerProps = {
  title: string;
  emptyText: string;
  accepts: (itemId: number) => boolean;
  onSelect: (itemId: number) => void;
  onClose: () => void;
  version: number;
};

function ForgeInventoryPicker({
  title,
  emptyText,
  accepts,
  onSelect,
  onClose,
  version: _version,
}: ForgeInventoryPickerProps) {
  const client = useClient();
  void _version;
  const items = client.inventoryController.items.filter(
    (item) => item.amount > 0 && accepts(item.id),
  );

  return (
    <div class='rounded border border-primary/60 bg-base-300/80 p-3'>
      <div class='mb-2 flex items-center justify-between gap-2'>
        <span class='text-xs font-semibold uppercase tracking-wide text-primary'>
          {title}
        </span>
        <button
          type='button'
          class='rounded px-2 py-0.5 text-[10px] opacity-70 hover:bg-base-content/10 hover:opacity-100'
          onClick={onClose}
        >
          Close
        </button>
      </div>
      {items.length === 0 ? (
        <div class='rounded bg-base-200 p-3 text-center text-xs opacity-70'>
          {emptyText}
        </div>
      ) : (
        <div class='grid max-h-56 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2'>
          {items.map((item) => {
            const record = client.getEifRecordById(item.id);
            return (
              <button
                key={item.id}
                type='button'
                class={`flex items-center gap-2 rounded border ${UI_PANEL_BORDER} bg-base-200 p-2 text-left hover:border-primary hover:bg-primary/10`}
                onClick={() => onSelect(item.id)}
              >
                {record && (
                  <span
                    class={`flex h-9 w-9 shrink-0 items-center justify-center rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-1`}
                  >
                    <ItemIcon
                      graphicId={record.graphicId}
                      alt={record.name}
                      class='max-h-full max-w-full object-contain'
                    />
                  </span>
                )}
                <span class='min-w-0 flex-1'>
                  <span class='block truncate text-xs font-semibold'>
                    {client.forgeController.getItemEnhancement(item.id,false)?.display_name || record?.name || `Item #${item.id}`}
                  </span>
                  <span class='block text-[10px] opacity-65'>
                    #{item.id} · x{item.amount}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function RonForgeDialog() {
  const client = useClient();
  const [targetItemId, setTargetItemId] = useState(0);
  const [materialItemId, setMaterialItemId] = useState(0);
  const [statGemCount, setStatGemCount] = useState(1);
  const [crystalId, setCrystalId] = useState(0);
  const [crystalCount, setCrystalCount] = useState(0);
  const [pickerSlot, setPickerSlot] = useState<ForgeDropSlot | null>(null);
  const [inventoryVersion, setInventoryVersion] = useState(0);
  const [action, setAction] = useState<ForgeAction>('upgrade');
  const [openData, setOpenData] = useState<ForgeOpenPayload>(
    () => client.forgeController.openData ?? FALLBACK_FORGE_DATA,
  );
  const [messages, setMessages] = useState<ForgeReply[]>(
    () => client.forgeController.messages,
  );

  useEffect(() => {
    const handleOpened = (payload: ForgeOpenPayload) => {
      setOpenData({ ...FALLBACK_FORGE_DATA, ...payload });
      setMessages([...client.forgeController.messages]);
    };
    const handleReply = (reply: ForgeReply) => {
      setMessages([...client.forgeController.messages]);
      if (reply.item?.id) {
        setTargetItemId(reply.item.id);
      }
      if (
        reply.success &&
        materialItemId > 0 &&
        client.inventoryController.getItemAmount(materialItemId) <= 0
      ) {
        setMaterialItemId(0);
      }
    };
    const handleDrop = (slot: ForgeDropSlot, itemId: number) => {
      if (slot === 'target') {
        if (!isForgeableTarget(itemId)) {
          client.toastController.showWarning('That item cannot be forged.');
          return;
        }
        setTargetItemId(itemId);
        return;
      }

      if (!isValidMaterial(itemId)) {
        client.toastController.showWarning('That material does not match the selected forge service.');
        return;
      }
      setMaterialItemId(itemId);
      setStatGemCount(1);
    };

    client.forgeController.subscribeOpened(handleOpened);
    client.forgeController.subscribeReply(handleReply);
    client.forgeController.subscribeDrop(handleDrop);

    return () => {
      client.forgeController.unsubscribeOpened(handleOpened);
      client.forgeController.unsubscribeReply(handleReply);
      client.forgeController.unsubscribeDrop(handleDrop);
    };
  }, [client, openData, materialItemId, action]);

  useEffect(() => {
    const handleInventoryChanged = () => setInventoryVersion((version) => version + 1);
    client.inventoryController.subscribeInventoryChanged(handleInventoryChanged);
    return () => {
      client.inventoryController.unsubscribeInventoryChanged(handleInventoryChanged);
    };
  }, [client]);

  const forgeData = { ...FALLBACK_FORGE_DATA, ...openData };
  const elementMappings = forgeData.element_mappings?.length
    ? forgeData.element_mappings
    : FALLBACK_FORGE_DATA.element_mappings!;
  const statGemMappings = forgeData.stat_gem_mappings?.length
    ? forgeData.stat_gem_mappings
    : FALLBACK_FORGE_DATA.stat_gem_mappings!;
  const elementMaterialIds = useMemo(
    () => new Set(elementMappings.map((mapping) => mapping.item_id)),
    [elementMappings],
  );
  const statGemMaterialIds = useMemo(
    () => new Set(statGemMappings.map((mapping) => mapping.item_id)),
    [statGemMappings],
  );
  const targetRecord =
    targetItemId > 0 ? client.getEifRecordById(targetItemId) : undefined;
  const materialRecord =
    materialItemId > 0 ? client.getEifRecordById(materialItemId) : undefined;
  const targetSlot = targetRecord ? slotForItemType(targetRecord.type) : null;
  const lunaGemId = forgeData.luna_gem_item_id ?? FALLBACK_FORGE_DATA.luna_gem_item_id!;
  const selectedElement = elementMappings.find((mapping) => mapping.item_id === materialItemId);
  const selectedStatGem = statGemMappings.find((mapping) => mapping.item_id === materialItemId);
  const statGemMaxCount = forgeData.stat_gem_max_count ?? FALLBACK_FORGE_DATA.stat_gem_max_count ?? 5;
  const selectedGemAmount = materialItemId > 0
    ? client.inventoryController.getItemAmount(materialItemId)
    : 0;
  const maxSelectableStatGemCount = Math.max(
    1,
    Math.min(statGemMaxCount, selectedGemAmount || statGemMaxCount),
  );
  const availableCrystals = elementMappings.filter(m => m.item_id !== 11 && client.inventoryController.getItemAmount(m.item_id)>0);
  const crystalLimit = Math.min(forgeData.crystal_max_count ?? 10,client.inventoryController.getItemAmount(crystalId));
  const safeCrystalCount = Math.max(0,Math.min(crystalCount,crystalLimit));
  const boost = safeCrystalCount*(forgeData.crystal_success_bonus_percent ?? 2);
  const boostChance = (base:number) => base>=100 ? 100 : Math.min(95,Math.max(0,base+boost));
  const selectedStatGemChance = boostChance(statGemSuccessChance(forgeData, statGemCount));
  const failureDescription = (chance:number) => {
    const fail=100-chance, wipe=forgeData.failure_wipe_percent ?? 10;
    const partial=(100-wipe)/2;
    return `Per attempt: ${(fail*wipe/100).toFixed(2)}% total reset (+0; ALL forged stats and sockets removed); ${(fail*partial/100).toFixed(2)}% lose 1 level; ${(fail*partial/100).toFixed(2)}% lose 2 levels. Partial failures keep sockets and random stats. Level cannot go below 0. The base item is never destroyed.`;
  };
  const crystalCostText = safeCrystalCount>0 ? `Also sacrifices ${safeCrystalCount} ${getDisplayName(client,crystalId)} (+${boost}% success, capped at 95%).` : 'No optional crystals sacrificed.';
  const targetEnhancement = client.forgeController.getForgeTargetEnhancement(targetItemId);
  const currentUpgradeLevel =
    targetEnhancement?.upgrade_level ??
    visibleUpgradeLevel(targetRecord?.name);
  const nextUpgradeLevel = currentUpgradeLevel + 1;
  const nextUpgradeRule = forgeData.upgrade_rules?.find(
    (rule) => rule.target_level === nextUpgradeLevel,
  );

  useEffect(() => {
    setStatGemCount((count) => Math.max(1, Math.min(count, maxSelectableStatGemCount)));
  }, [maxSelectableStatGemCount]);

  function isForgeableTarget(itemId: number): boolean {
    const record = client.getEifRecordById(itemId);
    return Boolean(record && slotForItemType(record.type));
  }

  function isValidMaterialForAction(itemId: number, selectedAction = action): boolean {
    if (selectedAction === 'upgrade') {
      return itemId === lunaGemId;
    }
    if (selectedAction === 'element') {
      return elementMaterialIds.has(itemId);
    }
    if (selectedAction === 'stat') {
      return statGemMaterialIds.has(itemId);
    }
    return false;
  }

  function isValidMaterial(itemId: number): boolean {
    return isValidMaterialForAction(itemId);
  }

  const validateTarget = useCallback(() => {
    if (!targetItemId || !targetRecord || !targetSlot) {
      client.toastController.showWarning('Place a weapon or armor piece in the gear slot first.');
      return false;
    }
    return true;
  }, [client, targetItemId, targetRecord, targetSlot]);

  const handleStatus = useCallback(() => {
    if (!validateTarget()) return;
    client.forgeController.status(targetItemId);
  }, [client, targetItemId, validateTarget]);

  const handleUpgrade = useCallback(() => {
    if (!validateTarget() || !targetSlot) return;
    if (materialItemId !== lunaGemId) {
      client.toastController.showWarning('Place a Luna Gem in the material slot for upgrades.');
      return;
    }

    const sendUpgrade = () => client.forgeController.upgrade(targetSlot, targetItemId, crystalId, safeCrystalCount);
    const itemName =
      client.forgeController.getForgeTargetEnhancement(targetItemId)?.display_name ||
      targetRecord?.name ||
      `Item #${targetItemId}`;
    const currentLevel =
      client.forgeController.getForgeTargetEnhancement(targetItemId)?.upgrade_level ??
      visibleUpgradeLevel(targetRecord?.name);
    const targetLevel = currentLevel + 1;
    const rule = forgeData.upgrade_rules?.find(
      (upgradeRule) => upgradeRule.target_level === targetLevel,
    );

    if (!rule || forgeData.protocol_version !== 2) {
      client.toastController.showWarning('Reopen the forge to load current upgrade costs and odds.');return;
    }

    const actualChance = boostChance(rule.success_percent);
    const failPercent = Math.max(0, 100 - actualChance);
    const resetText =
      failPercent > 0
        ? failureDescription(actualChance)
        : 'This upgrade cannot fail.';

    client.alertController.showConfirm(
      'Confirm Forge Upgrade',
      [
        `Upgrade: ${itemName} → +${targetLevel}`,
        `Success chance: ${actualChance}%`,
        crystalCostText,
        `Failure chance: ${failPercent}%`,
        resetText,
        `Cost: ${formatGold(rule.gold_cost)} gold + ${rule.luna_gem_cost} Luna Gem(s)`,
        'Gold and materials are spent when you continue.',
      ].join('\n'),
      (confirmed) => {
        if (confirmed) sendUpgrade();
      },
    );
  }, [
    client,
    targetItemId,
    targetRecord,
    targetSlot,
    materialItemId,
    lunaGemId,
    forgeData,
    crystalId, safeCrystalCount,
    validateTarget,
  ]);

  const handleAddElement = useCallback(() => {
    if (!validateTarget()) return;
    if (targetSlot !== 'weapon') {
      client.toastController.showWarning('Only weapons can receive elements right now.');
      return;
    }
    if (!selectedElement) {
      client.toastController.showWarning('Place an elemental crystal or orb in the material slot.');
      return;
    }

    const itemName =
      client.forgeController.getForgeTargetEnhancement(targetItemId)?.display_name ||
      targetRecord?.name ||
      `Item #${targetItemId}`;
    const materialName =
      materialRecord?.name ||
      selectedElement.label ||
      `Item #${materialItemId}`;
    const elementName = getElementName(selectedElement);

    client.alertController.showConfirm(
      'Confirm Add Element',
      [
        `Item: ${itemName}`,
        `Material: ${materialName}`,
        `Result: adds ${elementName} +${selectedElement.power}`,
        `Cost: ${formatGold(forgeData.element_add_gold_cost ?? 1000)} gold + 1 material`,
        'Crystals/orbs are consumed when you continue.',
      ].join('\n'),
      (confirmed) => {
        if (confirmed) {
          client.forgeController.addElement('weapon', targetItemId, materialItemId);
        }
      },
    );
  }, [
    client,
    targetItemId,
    targetRecord,
    targetSlot,
    materialItemId,
    materialRecord,
    selectedElement,
    forgeData.element_add_gold_cost,
    validateTarget,
  ]);

  const handleAddRandomStat = useCallback(() => {
    if (!validateTarget() || !targetSlot) return;
    if (!selectedStatGem) {
      client.toastController.showWarning('Place a colored stat gem in the material slot.');
      return;
    }

    const clampedGemCount = Math.max(1, Math.min(statGemCount, maxSelectableStatGemCount));
    const itemName =
      client.forgeController.getForgeTargetEnhancement(targetItemId)?.display_name ||
      targetRecord?.name ||
      `Item #${targetItemId}`;
    const materialName =
      materialRecord?.name ||
      selectedStatGem.label ||
      `Item #${materialItemId}`;
    const successChance = boostChance(statGemSuccessChance(forgeData, clampedGemCount));
    const failChance = Math.max(0, 100 - successChance);
    const goldCost = (forgeData.stat_gem_gold_cost ?? 750) * clampedGemCount;

    client.alertController.showConfirm(
      'Confirm Random Stat Roll',
      [
        `Item: ${itemName}`,
        `Material: ${clampedGemCount}x ${materialName}`,
        `Possible stat: ${formatStatTypes(selectedStatGem)}`,
        `Roll size: +${selectedStatGem.min_value} to +${selectedStatGem.max_value}`,
        `Success chance: ${successChance}%`,
        `Failure chance: ${failChance}%`,
        `Cost: ${formatGold(goldCost)} gold + ${clampedGemCount} gem(s)`,
        failureDescription(successChance),
        crystalCostText,
      ].join('\n'),
      (confirmed) => {
        if (confirmed) {
          client.forgeController.addRandomStat(
            targetSlot,
            targetItemId,
            materialItemId,
            clampedGemCount, crystalId, safeCrystalCount,
          );
        }
      },
    );
  }, [
    client,
    targetItemId,
    targetRecord,
    targetSlot,
    materialItemId,
    materialRecord,
    selectedStatGem,
    statGemCount,
    maxSelectableStatGemCount,
    forgeData,
    crystalId, safeCrystalCount,
    validateTarget,
  ]);

  const handleRemoveElements = useCallback(() => {
    if (!validateTarget()) return;
    if (targetSlot !== 'weapon') {
      client.toastController.showWarning('Only weapons can have elements removed.');
      return;
    }
    const itemName =
      client.forgeController.getForgeTargetEnhancement(targetItemId)?.display_name ||
      targetRecord?.name ||
      `Item #${targetItemId}`;

    client.alertController.showConfirm(
      'Confirm Remove Elements',
      [
        `Item: ${itemName}`,
        'Result: removes all elemental bonuses from this weapon.',
        `Cost: ${formatGold(forgeData.element_remove_gold_cost ?? 500)} gold`,
        'This action does not currently have a failure roll.',
      ].join('\n'),
      (confirmed) => {
        if (confirmed) {
          client.forgeController.removeElement('weapon', targetItemId);
        }
      },
    );
  }, [
    client,
    targetItemId,
    targetRecord,
    targetSlot,
    forgeData.element_remove_gold_cost,
    validateTarget,
  ]);

  const resetHint = `Reset Stats costs ${formatGold(forgeData.stat_reset_gold_cost ?? 0)} gold. Clears gem-added stats and frees stat sockets. Keeps the item, + level, base stats and elements. No gem refunds; new stat gems are needed to rebuild.`;
  const handleResetStats = () => {
    if (!validateTarget() || !targetSlot) return;
    if (!forgeData.stat_reset_gold_cost) {client.toastController.showWarning('Reopen Ron to load the stat reset price.');return;}
    if (!targetEnhancement?.options?.length) {client.toastController.showWarning('This item has no forged stat bonuses to reset.');return;}
    const id=targetItemId, slot=targetSlot;
    client.alertController.showConfirm('Reset forged stats?', `${targetEnhancement.display_name}\n\n${resetHint}\n\nThis cannot be undone.`, confirmed=>{
      if (confirmed) client.forgeController.resetStats(slot,id);
    });
  };
  const recycleWarning = 'One gear item is permanently consumed on every attempt. 5% chance for ONE gem or crystal; otherwise receive 2–20 gold. No gold cost. Shop-sold gear, peasant clothing, equipped and forged gear cannot be recycled.';
  const handleRecycle = () => {
    if (!validateTarget() || !targetSlot) return;
    if (!forgeData.recycling_enabled) {client.toastController.showWarning('Reopen Ron to load recycling.');return;}
    if (client.inventoryController.getItemAmount(targetItemId)<1 || currentUpgradeLevel>0 || targetEnhancement?.instance_id) {
      client.toastController.showWarning('Pick normal, unequipped gear. Forged gear is protected.');return;
    }
    const id=targetItemId, slot=targetSlot;
    client.alertController.showConfirm('Permanently recycle this gear?', `Recycle ONE ${targetRecord?.name || 'gear item'}?\n\n${recycleWarning}`, confirmed=>{
      if (confirmed) client.forgeController.recycle(slot,id);
    });
  };

  const primaryAction = {
    upgrade: handleUpgrade,
    element: handleAddElement,
    stat: handleAddRandomStat,
    remove: handleRemoveElements,
    recycle: handleRecycle,
    resetstats: handleResetStats,
  }[action];

  const actionHint = action === 'resetstats' ? resetHint : action === 'recycle' ? recycleWarning :
    action === 'upgrade'
      ? nextUpgradeRule
        ? `Next upgrade +${nextUpgradeLevel}: ${boostChance(nextUpgradeRule.success_percent)}% success / ${Math.max(
            0,
            100 - boostChance(nextUpgradeRule.success_percent),
          )}% fail. ${failureDescription(boostChance(nextUpgradeRule.success_percent))} Materials are spent either way.`
        : `Upgrade uses gold and Luna Gems. Maximum +${forgeData.max_upgrade_level ?? 100}. No safe checkpoint. Materials are spent when confirmed.`
      : action === 'element'
        ? `Add Element costs ${formatGold(forgeData.element_add_gold_cost ?? 1000)} gold and one elemental crystal/orb.`
        : action === 'stat'
          ? `Random Stats cost ${formatGold(forgeData.stat_gem_gold_cost ?? 750)} gold per gem. More gems raises success chance up to ${forgeData.stat_gem_max_success_percent ?? 90}%.`
        : `Remove Elements costs ${formatGold(forgeData.element_remove_gold_cost ?? 500)} gold and removes every element on the weapon.`;

  const materialHint =
    action === 'upgrade'
      ? `Tap or drag Luna Gem here. ${getDisplayName(client, lunaGemId)} is item #${lunaGemId}.`
      : action === 'element'
        ? 'Tap or drag an elemental crystal/orb here.'
        : action === 'stat'
          ? 'Tap or drag a colored gem here for random stats.'
          : 'No material is needed for removal.';

  return (
    <DialogBase id='ronForge' title={forgeData.title || 'Ron The Smith Forge'} size='xl' avoidBottom>
      <div class='grid max-h-[calc(80vh-36px)] gap-4 overflow-y-auto p-4 text-sm lg:grid-cols-[minmax(240px,0.82fr)_minmax(360px,1.18fr)]'>
        <div class='flex min-w-0 flex-col gap-3 rounded border border-base-content/10 bg-base-300/35 p-3'>
          <div>
            <div class='font-semibold'>Ron The Smith</div>
            <p class='m-0 mt-1 text-xs leading-relaxed opacity-75'>
              {forgeData.message || FALLBACK_FORGE_DATA.message}
            </p>
          </div>

          <div class='flex flex-col gap-1'>
            <span class='font-semibold text-xs uppercase tracking-wide opacity-75'>
              Service
            </span>
            <div class='grid grid-cols-1 gap-2 sm:grid-cols-2'>
              {[
                ['upgrade', 'Upgrade Item'],
                ['element', 'Add Element'],
                ['stat', 'Random Stats'],
                ['remove', 'Remove Elements'],
                ...(forgeData.stat_reset_gold_cost ? [['resetstats', 'Reset Stats']] : []),
                ...(forgeData.recycling_enabled ? [['recycle', 'Recycle Gear']] : []),
              ].map(([id, label]) => (
                <button
                  key={id}
                  type='button'
                  class={`min-h-10 rounded border px-3 py-2 text-center text-xs leading-tight ${
                    action === id
                      ? 'border-primary bg-primary text-primary-content'
                      : `${UI_PANEL_BORDER} bg-base-200 hover:bg-base-content/10`
                  }`}
                  onClick={() => {
                    const nextAction = id as ForgeAction;
                    setAction(nextAction);
                    if (nextAction === 'remove' || nextAction === 'recycle' || nextAction === 'resetstats') {
                      setMaterialItemId(0);
                    } else if (
                      materialItemId > 0 &&
                      !isValidMaterialForAction(materialItemId, nextAction)
                    ) {
                      setMaterialItemId(0);
                    }
                    setStatGemCount(1);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div class='rounded bg-base-200 p-3 text-xs leading-relaxed opacity-85'>
            {actionHint}
            <br />
            Weapons can hold up to {forgeData.weapon_max_element_slots ?? 2} elements.
            The second element unlocks at +{forgeData.second_element_unlock_level ?? 5}.
          </div>

          {action === 'element' && selectedElement && (
            <div class='rounded bg-base-200 p-2 text-xs'>
              Selected element:{' '}
              <span class='font-semibold'>
                {selectedElement.element_name ||
                  getElementName(selectedElement)}
                +{selectedElement.power}
              </span>
            </div>
          )}

          {action === 'stat' && selectedStatGem && (
            <div class='rounded bg-base-200 p-2 text-xs leading-relaxed'>
              <div>
                Selected stat gem:{' '}
                <span class='font-semibold'>
                  {materialRecord?.name || selectedStatGem.label || `Item #${materialItemId}`}
                </span>
              </div>
              <div>Possible stat: {formatStatTypes(selectedStatGem)}</div>
              <div>
                Roll: +{selectedStatGem.min_value} to +{selectedStatGem.max_value}
              </div>
              <div class='mt-2 flex flex-wrap items-center gap-2'>
                <span class='opacity-75'>Gems used:</span>
                <button
                  type='button'
                  class='rounded bg-base-content/10 px-2 py-1 font-semibold'
                  onClick={() => setStatGemCount((count) => Math.max(1, count - 1))}
                >
                  -
                </button>
                <span class='min-w-8 text-center font-semibold'>{statGemCount}</span>
                <button
                  type='button'
                  class='rounded bg-base-content/10 px-2 py-1 font-semibold'
                  onClick={() =>
                    setStatGemCount((count) => Math.min(maxSelectableStatGemCount, count + 1))
                  }
                >
                  +
                </button>
                <span class='text-success'>
                  {selectedStatGemChance}% success
                </span>
              </div>
            </div>
          )}

          <div class='max-h-36 overflow-y-auto rounded bg-base-300 p-3 text-xs leading-relaxed'>
            {messages.length === 0 ? (
              <div class='opacity-70'>No forge messages yet.</div>
            ) : (
              messages.map((reply, index) => (
                <div
                  key={`${index}-${reply.message}`}
                  class={reply.success ? 'text-success' : 'text-warning'}
                >
                  {reply.message}
                </div>
              ))
            )}
          </div>
        </div>

        <div class='flex min-w-0 flex-col gap-4'>
          <ForgeDropSlotView
            slot='target'
            title='Gear Slot'
            hint='Drag a weapon, armor, shield, hat, boots, or gloves here.'
            itemId={targetItemId}
            enhancement={targetEnhancement}
            accepts={isForgeableTarget}
            onClear={() => setTargetItemId(0)}
            onPick={() => setPickerSlot('target')}
          />

          {action !== 'recycle' && action !== 'resetstats' && <ForgeDropSlotView
            slot='material'
            title='Material Slot'
            hint={materialHint}
            itemId={materialItemId}
            accepts={action === 'remove' ? () => false : isValidMaterial}
            onClear={() => setMaterialItemId(0)}
            onPick={() => {
              if (action !== 'remove') setPickerSlot('material');
            }}
          />}

          {pickerSlot && (
            <ForgeInventoryPicker
              title={pickerSlot === 'target' ? 'Pick gear' : 'Pick material'}
              emptyText={
                pickerSlot === 'target'
                  ? 'No forgeable gear found in your inventory.'
                  : 'No matching material found in your inventory.'
              }
              accepts={pickerSlot === 'target' ? isForgeableTarget : isValidMaterial}
              onSelect={(itemId) => {
                if (pickerSlot === 'target') {
                  setTargetItemId(itemId);
                } else {
                  setMaterialItemId(itemId);
                  setStatGemCount(1);
                }
                setPickerSlot(null);
              }}
              onClose={() => setPickerSlot(null)}
              version={inventoryVersion}
            />
          )}

          {(action==='upgrade' || action==='stat') && (
            <div class='rounded border border-primary/40 bg-base-300 p-3 text-xs space-y-2'>
              <div class='font-semibold'>Optional: sacrifice crystals for better odds</div>
              <select aria-label='Success boost crystal' class='select select-bordered w-full' value={crystalId} onChange={e=>{setCrystalId(Number(e.currentTarget.value));setCrystalCount(0);}}>
                <option value={0}>No extra crystals</option>
                {availableCrystals.map(m=><option value={m.item_id}>{getDisplayName(client,m.item_id)} — {client.inventoryController.getItemAmount(m.item_id)} owned</option>)}
              </select>
              <label class='flex items-center gap-2'>Quantity
                <input aria-label='Crystals to sacrifice' class='input input-bordered w-24' type='number' min={0} max={crystalLimit} value={safeCrystalCount} onInput={e=>setCrystalCount(Math.max(0,Math.min(crystalLimit,Number(e.currentTarget.value)||0)))}/>
                <span>+{boost}% success</span>
              </label>
              <div>Crystals, gold, and required gems are consumed on success or failure.</div>
            </div>
          )}
          {targetEnhancement && <div class='rounded bg-base-300 p-2 text-xs'>Stat gem sockets: {targetEnhancement.stat_sockets_used ?? 0} / {targetEnhancement.stat_sockets_max ?? 6}. Element sockets: {targetEnhancement.elements.length} / 2. Legacy bonuses are preserved.</div>}
          <div class='grid grid-cols-1 gap-2 sm:grid-cols-2'>
            <Button variant='secondary' onClick={handleStatus}>
              Check Item
            </Button>
            <Button variant='primary' onClick={primaryAction}>
              {action === 'resetstats' ? 'Reset Stats' : action === 'recycle' ? 'Recycle One Item' : action === 'upgrade'
                ? 'Forge Upgrade'
                : action === 'element'
                  ? 'Add Element'
                  : action === 'stat'
                    ? 'Roll Random Stat'
                    : 'Remove Elements'}
            </Button>
          </div>

          {materialRecord && (
            <div class='rounded bg-base-200 p-2 text-[11px] opacity-75'>
              Material selected: {materialRecord.name}
              {action === 'stat' ? ` x${statGemCount}` : ''}
            </div>
          )}
        </div>
      </div>
    </DialogBase>
  );
}
